import { v4 as uuid } from 'uuid';

import { ArticleDefinition } from '../../interfaces/Article';
import {
  MessageNotification,
  MessageWithLinkNotification,
  MessageWithProductSelection,
  MultipleMessagesNotification,
  Notification,
  NotificationTheme,
  NotificationType,
} from '../../interfaces/Notification';
import { RequiredProductDelta } from '../../store/configuration/replace/calculateProductDelta';

export class NotificationFactory {
  static DEFAULT_TIMEOUT_DURATION = 10;

  static customNegative = (message: string): MessageNotification =>
    NotificationFactory.custom(
      message,
      NotificationTheme.NEGATIVE,
      NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    );

  static customPositive = (message: string): MessageNotification =>
    NotificationFactory.custom(
      message,
      NotificationTheme.POSITIVE,
      NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    );

  static custom = (
    message: string,
    theme: NotificationTheme,
    timeout?: number,
  ): MessageNotification => ({
    id: uuid(),
    title: undefined,
    type: NotificationType.MESSAGE,
    timeout,
    theme,
    message,
  });

  static automaticallyAddedRequiredArticle = (
    requiredProductDeltas: RequiredProductDelta[],
  ): Notification => {
    if (requiredProductDeltas.length === 1) {
      return {
        id: uuid(),
        timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
        type: NotificationType.MESSAGE,
        theme: NotificationTheme.DEFAULT,
        message: 'AUTOMATICALLY_ADDED_PRODUCT',
        placeholder: requiredProductDeltas[0].representativeArticle.definition.displayName,
      };
    }

    return {
      id: uuid(),
      timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
      type: NotificationType.MESSAGE_WITH_LIST,
      theme: NotificationTheme.DEFAULT,
      message: 'AUTOMATICALLY_ADDED_PRODUCTS',
      list: requiredProductDeltas.map(
        (delta) => delta.representativeArticle.definition.displayName,
      ),
    };
  };

  static configurationCannotBeOpened = (): Notification => ({
    id: uuid(),
    timeout: 0,
    type: NotificationType.MESSAGE_WITH_LIST,
    theme: NotificationTheme.NEGATIVE,
    message: 'CONFIGURATION_CANNOT_BE_OPENED',
    list: [
      'CONFIGURATION_NO_ACCESS_RIGHTS',
      'CONFIGURATION_NOT_LOGGED_IN',
      'CONFIGURATION_DOES_NOT_EXIST',
    ],
  });

  static noAvailableSlotToAddProduct = (productName: string): MessageNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE,
    theme: NotificationTheme.NEGATIVE,
    title: 'AUTOMATIC_PRODUCT_CANNOT',
    message: 'CANNOT_ADD_NO_FREE_POSITION',
    placeholder: productName,
  });

  static noAvailableSlotToAddArticleWithParents = (
    productName: string,
    articles: ArticleDefinition[],
  ): MessageWithProductSelection => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE_WITH_SELECTION,
    theme: NotificationTheme.NEGATIVE,
    title: 'AUTOMATIC_PRODUCT_CANNOT',
    message: 'CANNOT_ADD_NO_REQUIRED_PARENT',
    placeholder: productName,
    articles: articles,
  });

  static productCannotBeAddedAutomaticallyCustom = (message: string): MessageNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE,
    theme: NotificationTheme.NEGATIVE,
    title: 'AUTOMATIC_PRODUCT_CANNOT',
    message: message,
  });

  static productCannotBeAddedAutomatically = (productName: string): MessageNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE,
    theme: NotificationTheme.NEGATIVE,
    title: 'AUTOMATIC_PRODUCT_CANNOT',
    message: 'CANNOT_ADD_PRODUCT',
    placeholder: productName,
  });

  static projectCreated = (
    projectCode: string,
    projectTitle: string,
    url: string,
  ): MessageWithLinkNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE_WITH_LINK,
    theme: NotificationTheme.POSITIVE,
    title: undefined,
    message: 'CONFIGURATION_IN_PROJECT_HAS_BEEN_SAVED',
    placeholder: `${projectCode} - ${projectTitle}`,
    url: url,
  });

  static projectCreatedWithManyItems = (
    projectCode: string,
    projectTitle: string,
    url: string,
  ): MessageWithLinkNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE_WITH_LINK,
    theme: NotificationTheme.POSITIVE,
    title: undefined,
    message: 'CONFIGURATIONS_IN_PROJECT_HAS_BEEN_SAVED',
    placeholder: `${projectCode} - ${projectTitle}`,
    url: url,
  });

  static articleCannotBeMoved = (articleName: string): MessageNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE,
    theme: NotificationTheme.NEGATIVE,
    title: undefined,
    message: 'PRODUCT_CANNOT_BE_MOVED',
    placeholder: articleName,
  });

  static projectNotCreated = (): MessageNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE,
    theme: NotificationTheme.NEGATIVE,
    title: undefined,
    message: 'PROJECT_NOT_CREATED',
  });

  static couldNotRequestConfiguration = (): MessageNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE,
    theme: NotificationTheme.NEGATIVE,
    title: undefined,
    message: 'PROJECT_NOT_REQUESTED',
  });

  static configurationSuccessfullyRequested = (
    currentDate: string,
    url: string,
  ): MessageWithLinkNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE_WITH_LINK,
    theme: NotificationTheme.POSITIVE,
    title: undefined,
    message: 'REQUEST_INFO',
    placeholder: currentDate,
    url: url,
  });

  static anonymousConfigurationSuccessfullyRequested = (
    currentDate: string,
  ): MessageNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MESSAGE,
    theme: NotificationTheme.POSITIVE,
    title: undefined,
    message: 'ANONYMOUS_REQUEST_INFO',
    placeholder: currentDate,
  });

  static multipleViolations = (messages: string[]): MultipleMessagesNotification => ({
    id: uuid(),
    timeout: NotificationFactory.DEFAULT_TIMEOUT_DURATION,
    type: NotificationType.MULTIPLE_MESSAGES,
    theme: NotificationTheme.NEGATIVE,
    title: undefined,
    message: undefined,
    messages: messages,
  });
}
